import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { WebsiteIdea, WebsiteName, IdeaStatus } from '@/types/website';

export function useWebsiteIdeas(websiteFilter?: WebsiteName) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: ideas = [], isLoading, error } = useQuery({
    queryKey: ['website-ideas', websiteFilter],
    queryFn: async () => {
      let query = supabase
        .from('website_ideas')
        .select(`
          *,
          created_profile:profiles!website_ideas_created_by_fkey(display_name, email)
        `)
        .order('impact_score', { ascending: false, nullsFirst: false });

      if (websiteFilter) {
        query = query.eq('website', websiteFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as WebsiteIdea[];
    },
    enabled: !!user,
  });

  const createIdea = useMutation({
    mutationFn: async (idea: {
      title: string;
      description?: string;
      website: WebsiteName;
      impact_score?: number;
      next_step?: string;
    }) => {
      const { data, error } = await supabase
        .from('website_ideas')
        .insert({
          ...idea,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-ideas'] });
      toast.success('Idee aangemaakt');
    },
    onError: (error) => {
      console.error('Error creating idea:', error);
      toast.error('Fout bij aanmaken idee');
    },
  });

  const updateIdea = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WebsiteIdea> & { id: string }) => {
      const { data, error } = await supabase
        .from('website_ideas')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-ideas'] });
      toast.success('Idee bijgewerkt');
    },
    onError: (error) => {
      console.error('Error updating idea:', error);
      toast.error('Fout bij bijwerken idee');
    },
  });

  const deleteIdea = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('website_ideas')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-ideas'] });
      toast.success('Idee verwijderd');
    },
    onError: (error) => {
      console.error('Error deleting idea:', error);
      toast.error('Fout bij verwijderen idee');
    },
  });

  return {
    ideas,
    isLoading,
    error,
    createIdea,
    updateIdea,
    deleteIdea,
  };
}
